import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { isMobile } from 'src/utils/devices';

import {
  getIsEditingForms,
  setIsEditingForms,
  saveLogFormsAction,
  getHosLog,
} from 'src/features/hosLogs/store';
import { getUserId } from 'src/features/profile/store';

import { Button } from 'src/components/Button';
import { HosFormsItem } from 'src/components/HosFormsItem';

import classnames from 'classnames';

import './hosForms.scss';

interface Props {
  powerUnitNumbers: [];
  trailerNumbers: [];
  shipNumbers: [];
  powerUnits: [
    {
      truckNumber: string;
      vin: string;
    },
  ];
  milesDriven: number;
  odometer: string;
  coDriver: string;
  adNotes: string;
  certifiedStatus: number;
}

interface FormValues {
  powerUnitNumbers: string[];
  trailerNumbers: string[];
  shipNumbers: string[];
  coDriver: string;
  adNotes: string;
  certifiedStatus: number;
}

export interface AccumulatorFormsPayload {
  assetInformation?: {
    powerUnitNumbers?: string[];
    trailerNumbers?: string[];
  };
  shippingNumbers?: string[];
  coDriver?: string;
  additionalNotes?: string;
}

type PrepareFormsPayload = {
  [key: string]: keyof AccumulatorFormsPayload | any;
};

export interface MultiValueFormFields {
  powerUnitNumbers: string[];
  trailerNumbers: string[];
  shipNumbers: string[];
}

export const HosForms: React.FC<Props> = ({
  powerUnitNumbers,
  trailerNumbers,
  shipNumbers,
  milesDriven,
  coDriver,
  adNotes,
  certifiedStatus,
  powerUnits,
  odometer,
}) => {
  const dispatch = useDispatch();

  const [isClearedValues, setIsClearedValues] = useState(false);
  const [formsValues, setFormsValues] = useState<FormValues | undefined>();
  const [multipleValue, setMultipleValue] = useState<MultiValueFormFields>({
    powerUnitNumbers,
    trailerNumbers,
    shipNumbers,
  });

  const isEditingForms = useSelector(getIsEditingForms);
  const employeeId = useSelector(getUserId);

  const isCertified = useSelector(getHosLog);

  const routeMatch = useRouteMatch<{ date: string }>();
  const dateParam = routeMatch.params.date;

  const modifiedValue = useCallback(
    value => {
      setFormsValues({ ...formsValues, ...value });
    },
    [formsValues],
  );

  const deleteArrayValue = (deleteValue: string, key: keyof MultiValueFormFields) => {
    setMultipleValue(
      (state): MultiValueFormFields => {
        const prepareState = Array.isArray(state[key])
          ? state[key].filter((item: string) => item !== deleteValue)
          : state;

        modifiedValue({ [key]: '' });

        return {
          ...state,
          [key]: prepareState,
        };
      },
    );
  };

  const handleEditForms = useCallback(() => {
    dispatch(setIsEditingForms(true));
    setIsClearedValues(false);
  }, [dispatch]);

  const preparePayloadForSave = (data: PrepareFormsPayload): AccumulatorFormsPayload => {
    const objectAccumulator: AccumulatorFormsPayload = {};

    for (const key in data) {
      if ({}.hasOwnProperty.call(data, key)) {
        switch (key) {
          case 'powerUnitNumbers':
            if (!objectAccumulator.assetInformation) objectAccumulator.assetInformation = {};
            objectAccumulator.assetInformation.powerUnitNumbers = data[key]
              ? [...multipleValue.powerUnitNumbers, data[key]]
              : multipleValue.powerUnitNumbers;
            break;
          case 'trailerNumbers':
            if (!objectAccumulator.assetInformation) objectAccumulator.assetInformation = {};
            objectAccumulator.assetInformation.trailerNumbers = data[key]
              ? [...multipleValue.trailerNumbers, data[key]]
              : multipleValue.trailerNumbers;
            break;
          case 'shipNumbers':
            objectAccumulator.shippingNumbers = data[key]
              ? [...multipleValue.shipNumbers, data[key]]
              : multipleValue.shipNumbers;
            break;
          case 'coDriver':
            objectAccumulator.coDriver = data[key];
            break;
          case 'adNotes':
            objectAccumulator.additionalNotes = data[key];
            break;
        }
      }
    }

    return objectAccumulator;
  };

  const handleSaveForms = useCallback(() => {
    if (formsValues) {
      const savePayload = preparePayloadForSave(formsValues);

      dispatch(
        saveLogFormsAction({
          employeeId,
          logDate: dateParam,
          formPayload: {
            ...savePayload,
            fmcsaLogDate: dateParam,
          },
        }),
      );
    }
  }, [dispatch, employeeId, dateParam, formsValues, milesDriven, odometer, powerUnits]);

  const handleCancelForms = useCallback(() => {
    setIsClearedValues(true);

    setMultipleValue({
      powerUnitNumbers,
      trailerNumbers,
      shipNumbers,
    });
  }, [dispatch]);

  const hosLogsFormsStyle = classnames('hos-logs-forms', {
    'hos-logs-forms__mobile': isMobile,
  });

  const buttonWrapperStyles = classnames({
    'hos-forms-button-wrapper': isMobile,
  });

  const formsButtonsStyle = classnames('hos-forms-button', {
    'hos-forms-button__mobile': isMobile,
  });

  const formsActionsButtons = classnames('hos-forms-action-buttons', {
    'hos-forms-action-buttons__mobile': isMobile,
  });

  const renderEditFormButtons = useCallback(() => {
    return (
      <>
        {!isEditingForms ? (
          <div className={buttonWrapperStyles}>
            <Button title="Edit Forms" onClick={handleEditForms} />
          </div>
        ) : (
          <div className={formsActionsButtons}>
            <Button onClick={handleSaveForms} className="hos-forms-save-button" title="Save" />
            <Button onClick={handleCancelForms} title="Cancel" />
          </div>
        )}
      </>
    );
  }, [
    isEditingForms,
    buttonWrapperStyles,
    formsActionsButtons,
    handleEditForms,
    handleCancelForms,
    handleSaveForms,
  ]);

  const renderFormsButtons = useCallback(() => {
    return (
      <>
        {!isCertified.certifiedStatus ? (
          <div className={formsButtonsStyle}>{renderEditFormButtons()}</div>
        ) : null}
      </>
    );
  }, [isCertified, formsButtonsStyle, renderEditFormButtons]);

  return (
    <div className={hosLogsFormsStyle}>
      {renderFormsButtons()}
      {certifiedStatus === 1 && (
        <div className="not-editable-warning">Log forms are not editable once certified.</div>
      )}
      <form name="logForm">
        <HosFormsItem
          title="Tractor/Truck Numbers"
          itemType="multiple"
          value={powerUnitNumbers}
          initialValue={powerUnitNumbers}
          onChange={(value: string | string[]) => modifiedValue({ powerUnitNumbers: value })}
          cleared={isClearedValues}
          multipleValue={multipleValue.powerUnitNumbers}
          itemKey={'powerUnitNumbers'}
          deleteArrayValue={deleteArrayValue}
        />

        <HosFormsItem
          title="Trailer Numbers"
          itemType="multiple"
          value={trailerNumbers}
          initialValue={trailerNumbers}
          onChange={(value: string | string[]) => modifiedValue({ trailerNumbers: value })}
          cleared={isClearedValues}
          multipleValue={multipleValue.trailerNumbers}
          itemKey={'trailerNumbers'}
          deleteArrayValue={deleteArrayValue}
        />

        <HosFormsItem
          title="Shipping Numbers"
          itemType="multiple"
          value={shipNumbers}
          initialValue={shipNumbers}
          onChange={(value: string | string[]) => modifiedValue({ shipNumbers: value })}
          cleared={isClearedValues}
          multipleValue={multipleValue.shipNumbers}
          itemKey={'shipNumbers'}
          deleteArrayValue={deleteArrayValue}
        />

        <HosFormsItem
          title="Miles Driven"
          itemType="non-editable"
          value={[String(milesDriven)]}
          initialValue={String(milesDriven)}
        />

        <HosFormsItem
          title="Co Driver"
          itemType="single"
          value={[coDriver]}
          initialValue={coDriver}
          onChange={(value: string | string[]) => modifiedValue({ coDriver: value })}
          cleared={isClearedValues}
        />

        <HosFormsItem
          title="Additional Notes"
          itemType="single"
          value={[adNotes]}
          initialValue={adNotes}
          onChange={(value: string | string[]) => modifiedValue({ adNotes: value })}
          cleared={isClearedValues}
        />
      </form>
    </div>
  );
};
